/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

import React, { useState } from 'react';
import { ITaskCreationFlowProps } from '../ModifyServices.Model';
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import theme from '../../../../../../Shared/Themes/theme';
import { buttonStyles } from '../../../../../Jobs/Components/UpdateShiftDate/UpdateShiftDate.Style';
import Button, { ButtonType } from '../../../../../../Shared/Components/Common/Buttons/Button';
import { JobNameContainer } from '../../../../../Jobs/Components/AddEditOneTimeJob/AddEditOneTimeJob.Style';
import CustomDropDownAutoComplete from '../../../../../../Shared/Components/Common/CustomDropDown/CustomDropdownAutoComplete';
import { Icons } from '../../../../../../Shared/Constants/Icons';
import { useTranslation } from 'react-i18next';
import ModifyPopUp from '../../../../../Jobs/Components/JobDetails/ModifyPopUp';
import { CreateTaskSubtaskMappingDto } from '../../../../../../Shared/Models';
import { ServiceCreationType, SUBTASK_TYPE } from '../../../../../../Shared/Constants/App';
import { styles } from '../ModifyServices.Style';
import { isEmpty } from 'lodash';

interface TaskForm {
  name: string;
  description: string;
}

interface SubTaskForm {
  name: string;
  description: string;
}

const CreateTaskForm: React.FC<ITaskCreationFlowProps> = (props) => {
  const {
    createSubTask,
    setCreateSubTask,
    setSelectedSubTask,
    taskOptions,
    handleSubTaskChanges,
    handlePaginationChange,
    isTasksLoading,
    totalCount,
    currentPage,
    searchString,
    setSearchString,
    selectedSubTaskType,
    handleSubTaskTypeChange,
    setAddNewTask,
    setNextClick,
    handleCreateTask,
    setServiceTaskMapping,
    handleServiceTaskMapping,
    setIsEditService,
    isServiceTaskLoading,
    handleTaskSubTaskMapping,
    subTask,
    subTasksObj,
    setSubTask,
    setSelectedOption,
    setSelectedServiceId,
  } = props;

  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState(false);
  const [isCreateSubTaskButtonLoading, setIsCreateSubTaskButtonLoading] = useState(false);
  const [isConfirmChanges, setIsConfirmChanges] = useState(false);
  const [taskForm, setTaskForm] = useState<TaskForm>({ name: '', description: '' });
  const [subTaskForm, setSubTaskForm] = useState<SubTaskForm>({ name: '', description: '' });
  const [selectedTaskType, setSelectedTaskType] = useState<string>('');

  const { radioButton, radioButtonText, textField, headingText } = buttonStyles;
  const { t } = useTranslation(['taskManagement']);

  const handleTaskTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedTaskType((event.target as HTMLInputElement).value);
  };

  const updateTaskForm = (field: keyof TaskForm, value: string) => {
    setTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const updateSubTaskForm = (field: keyof SubTaskForm, value: string) => {
    setSubTaskForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetAllForms = () => {
    setAddNewTask(false);
    setCreateSubTask(false);
    setIsEditService(false);
    setServiceTaskMapping(undefined);
    setTaskForm({ name: '', description: '' });
    setSubTaskForm({ name: '', description: '' });
    setNextClick(false);
    setSubTask('');
    setSearchString('');
    setSelectedOption('');
    setSelectedServiceId(null);
  };

  const createMainTaskData = (response: any) => ({
    mdTaskId: response?.mdTaskId ?? '',
    mdTaskName: response?.mdTaskName ?? '',
    mdTaskDescription: response?.mdTaskDescription ?? '',
    isProofOfCompletionRequired: false,
    isInspectionRequired: false,
    inspectionEntity: '',
    subTasks: [] as { mdTaskId: string; mdTaskName: string; mdTaskDescription: string }[],
  });

  const createTaskSubTaskPayload = (mainTaskResponse: any, subTaskData: any): CreateTaskSubtaskMappingDto => ({
    tasks: [
      {
        mdTaskId: mainTaskResponse?.mdTaskId || '',
        mdTaskName: mainTaskResponse?.mdTaskName || '',
        mdTaskDescription: mainTaskResponse?.mdTaskDescription || '',
        subTasks: [
          {
            mdTaskId: subTaskData.mdTaskId || '',
            mdTaskName: subTaskData.mdTaskName,
            mdTaskDescription: subTaskData.mdTaskDescription,
          },
        ],
      },
    ],
  });

  const addTaskToMapping = (mainTaskData: any) => {
    setServiceTaskMapping((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        type: selectedTaskType,
        tasks: [...(prev.tasks ?? []), mainTaskData],
      };
    });
  };

  const handleConfirmButton = async () => {
    try {
      setIsConfirmButtonLoading(true);

      if (!createSubTask) {
        const response = await handleCreateTask(taskForm.name, taskForm.description);
        if (!response) return;

        const mainTaskData = createMainTaskData(response);

        const selectedSubTaskObj = subTasksObj?.mdTaskId === subTask ? subTasksObj : {};

        const subTaskData = {
          mdTaskId: selectedSubTaskObj?.mdTaskId ?? '',
          mdTaskName: selectedSubTaskObj?.taskName ?? '',
          mdTaskDescription: selectedSubTaskObj?.taskDescription ?? '',
        };

        mainTaskData.subTasks.push(subTaskData);

        if (selectedSubTaskType === SUBTASK_TYPE.PERMANENT) {
          const payload = createTaskSubTaskPayload(response, subTaskData);
          handleTaskSubTaskMapping(payload);
        }

        addTaskToMapping(mainTaskData);
        setIsConfirmChanges(true);
      }
    } catch (e) {
      console.error('Error occurred while confirming task:', e);
    } finally {
      setIsConfirmButtonLoading(false);
    }
  };

  const handleCreateSubTask = async () => {
    try {
      setIsCreateSubTaskButtonLoading(true);

      if (createSubTask) {
        const mainTaskResponse = await handleCreateTask(taskForm.name, taskForm.description);
        if (!mainTaskResponse) return;

        const mainTaskData = createMainTaskData(mainTaskResponse);

        const subTaskResponse = await handleCreateTask(subTaskForm.name, subTaskForm.description);
        if (!subTaskResponse) return;

        const subTaskData = {
          mdTaskId: subTaskResponse?.mdTaskId,
          mdTaskName: subTaskForm.name,
          mdTaskDescription: subTaskForm.description,
          creationType: selectedSubTaskType,
        };

        (mainTaskData.subTasks as any[]).push(subTaskData as any);

        addTaskToMapping(mainTaskData);

        if (selectedSubTaskType === SUBTASK_TYPE.PERMANENT) {
          const payload = createTaskSubTaskPayload(mainTaskResponse, subTaskData);
          handleTaskSubTaskMapping(payload);
        }

        setIsConfirmChanges(true);
      }
    } catch (e) {
      console.error('Error occurred while creating subtask:', e);
    } finally {
      setIsCreateSubTaskButtonLoading(false);
    }
  };

  const renderRadioOptions = () => (
    <FormControl component="fieldset">
      <Typography
        sx={{
          fontFamily: theme.typography.fontFamily,
          fontSize: theme.typography.h3?.fontSize,
          fontWeight: theme.typography.fontWeightBold,
          marginTop: createSubTask ? 0 : '2rem',
        }}>
        <span>
          {t('taskManagement:DoYouWantToAddSubTaskPermanentlyOrSchedule')}
          <span className="mandatory-field"> *</span>
        </span>
      </Typography>
      <RadioGroup value={selectedSubTaskType} onChange={handleSubTaskTypeChange}>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={SUBTASK_TYPE.PERMANENT}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Permanently')}
            sx={radioButtonText}
            disabled={isEmpty(subTask)}
          />
        </Box>
        <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
          <FormControlLabel
            value={SUBTASK_TYPE.SCHEDULE}
            control={<Radio sx={radioButton} />}
            label={t('taskManagement:Schedule')}
            sx={radioButtonText}
            disabled={isEmpty(subTask)}
          />
        </Box>
      </RadioGroup>
    </FormControl>
  );

  const renderCreateSubTaskForm = () => (
    <Box sx={{ marginTop: '2rem' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-name"
            label={
              <span>
                {t('taskManagement:SubTaskName')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.name}
            onChange={(e) => updateSubTaskForm('name', e.target.value)}
          />
        </JobNameContainer>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="subtask-description"
            label={
              <span>
                {t('taskManagement:SubTaskDescription')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={subTaskForm.description}
            onChange={(e) => updateSubTaskForm('description', e.target.value)}
          />
        </JobNameContainer>
      </Box>
      {renderRadioOptions()}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
        <Button
          type={ButtonType.Secondary}
          label={t('taskManagement:Cancel')}
          onClick={() => setCreateSubTask(false)}
        />
        <Button
          type={ButtonType.Primary}
          label={t('taskManagement:CreateSubTaskButton')}
          onClick={handleCreateSubTask}
          disabled={!subTaskForm.name || !subTaskForm.description || !selectedSubTaskType}
          isLoading={isCreateSubTaskButtonLoading}
        />
      </Box>
    </Box>
  );

  const renderTaskRadioOptions = () => (
    <Box sx={{ marginBottom: '2rem' }}>
      <FormControl component="fieldset">
        <Typography
          sx={{
            fontFamily: theme.typography.fontFamily,
            fontSize: theme.typography.h3?.fontSize,
            fontWeight: theme.typography.fontWeightBold,
          }}>
          <span>
            {t('taskManagement:DoYouWantToAddTaskPermanentlyOrSchedule')}
            <span className="mandatory-field"> *</span>
          </span>
        </Typography>
        <RadioGroup value={selectedTaskType} onChange={handleTaskTypeChange}>
          <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
            <FormControlLabel
              value={ServiceCreationType.SCHEDULE_LEVEL}
              control={<Radio sx={radioButton} />}
              label={t('taskManagement:ScheduleLevel')}
              sx={radioButtonText}
            />
          </Box>
          <Box sx={{ marginLeft: '1rem', marginTop: '1rem' }}>
            <FormControlLabel
              value={ServiceCreationType.CUSTOMER_LEVEL}
              control={<Radio sx={radioButton} />}
              label={t('taskManagement:CustomerLevel')}
              sx={radioButtonText}
            />
          </Box>
        </RadioGroup>
      </FormControl>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography sx={headingText}>{t('taskManagement:Tasks')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="task-name"
            label={
              <span>
                {t('taskManagement:TaskName')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={taskForm.name}
            onChange={(e) => updateTaskForm('name', e.target.value)}
          />
        </JobNameContainer>
        <JobNameContainer>
          <TextField
            sx={textField}
            id="task-description"
            label={
              <span>
                {t('taskManagement:TaskDescription')}
                <span className="mandatory-field"> *</span>
              </span>
            }
            value={taskForm.description}
            onChange={(e) => updateTaskForm('description', e.target.value)}
          />
        </JobNameContainer>
      </Box>
      {renderTaskRadioOptions()}
      <Typography sx={headingText}>{t('taskManagement:SubTasks')}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2, alignItems: 'center' }}>
        <CustomDropDownAutoComplete
          title={t('taskManagement:SubTasks')}
          options={taskOptions}
          selectedValue={!createSubTask ? subTask : ''}
          onChange={handleSubTaskChanges}
          isLoading={isTasksLoading}
          totalCounts={totalCount}
          handlePaginationChanges={handlePaginationChange}
          currentPages={currentPage}
          searchString={searchString}
          setSearchString={setSearchString}
          dropDownHeight={500}
          isInline={true}
          isDisabled={createSubTask}
        />
        <Box
          sx={{
            ...styles.addNewLink,
            opacity: !taskForm.name || !taskForm.description ? '0.5' : '1',
            cursor: !taskForm.name || !taskForm.description ? 'not-allowed' : 'pointer',
          }}
          onClick={() => {
            if (!taskForm.name || !taskForm.description) return;
            setCreateSubTask(true);
            setSelectedSubTask('');
          }}>
          <img src={Icons.AddIcon} width={24} height={24} alt={t('altTexts:add')} />
          {t('taskManagement:AddNewSubTask')}
        </Box>
      </Box>

      {createSubTask && renderCreateSubTaskForm()}

      {!createSubTask && (
        <>
          {renderRadioOptions()}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem', gap: 4 }}>
            <Button
              type={ButtonType.Secondary}
              label={t('taskManagement:Cancel')}
              onClick={() => {
                setAddNewTask(false);
                setNextClick(false);
              }}
            />
            <Button
              type={ButtonType.Primary}
              label={t('taskManagement:Confirm')}
              onClick={handleConfirmButton}
              disabled={!taskForm.name || !taskForm.description || !selectedTaskType}
              isLoading={isConfirmButtonLoading}
            />
          </Box>
        </>
      )}

      <ModifyPopUp
        open={isConfirmChanges}
        handleClose={() => setIsConfirmChanges(false)}
        handleOnCloseJob={() => {
          handleServiceTaskMapping();
          setIsConfirmChanges(false);
          resetAllForms();
        }}
        loading={isServiceTaskLoading}
        heading={t('taskManagement:ConfirmChanges')}
        text={t('taskManagement:AreYouSureYouWantToConfirmThisChanges')}
      />
    </Box>
  );
};

export default CreateTaskForm;
